// src/contexts/AuthContext.jsx
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { login as loginService, register as registerService, logout as logoutService, getCurrentUser } from '../services/auth';

export const AuthContext = createContext();

// Check if a JWT token is expired (with 60-second buffer)
const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Expire 60 seconds early to avoid edge-case 401s
        return payload.exp * 1000 < Date.now() + 60000;
    } catch {
        return true;
    }
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const forceLogout = useCallback(() => {
        setCurrentUser(null);
        setError(null);
        logoutService();
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    }, []);

    useEffect(() => {
        // Initialize auth state on app load
        const initializeAuth = () => {
            const token = localStorage.getItem('accessToken');
            const userStr = localStorage.getItem('user');

            if (token && userStr) {
                // Check if token is already expired
                if (isTokenExpired(token)) {
                    console.log('AuthContext: Token expired, clearing auth');
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('user');
                } else {
                    try {
                        const userData = JSON.parse(userStr);
                        setCurrentUser(userData);
                    } catch (error) {
                        console.error('AuthContext: Error parsing stored user data:', error);
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('user');
                    }
                }
            }

            setLoading(false);
        };

        initializeAuth();
    }, []);

    // Periodically check token expiry (every 60 seconds)
    useEffect(() => {
        if (!currentUser) return;

        const interval = setInterval(() => {
            const token = localStorage.getItem('accessToken');
            if (isTokenExpired(token)) {
                console.log('AuthContext: Token expired during session, logging out');
                forceLogout();
            }
        }, 60000);

        return () => clearInterval(interval);
    }, [currentUser, forceLogout]);

    const handleLogin = async (credentials) => {
        setLoading(true);
        setError(null);
        try {
            console.log('AuthContext: Attempting login...');
            const response = await loginService(credentials);

            // Get the user data that was stored by the auth service
            const userData = getCurrentUser();
            console.log('AuthContext: Login successful, user data:', userData);

            setCurrentUser(userData);
            return userData;
        } catch (err) {
            console.error('AuthContext: Login failed:', err);
            setError(err.message || 'Login failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (userData) => {
        setLoading(true);
        setError(null);
        try {
            console.log('AuthContext: Attempting registration...');
            const result = await registerService(userData);

            // After registration, log the user in
            return await handleLogin({
                email: userData.email,
                password: userData.password
            });
        } catch (err) {
            console.error('AuthContext: Registration failed:', err);
            setError(err.message || 'Registration failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        console.log('AuthContext: Logging out user');
        setCurrentUser(null);
        setError(null);
        logoutService();
    };

    // Optional: Add token validation function
    const validateAuth = () => {
        const token = localStorage.getItem('accessToken');
        const userData = getCurrentUser();

        if (!token || !userData) {
            console.log('AuthContext: Authentication validation failed, clearing state');
            setCurrentUser(null);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('user');
            return false;
        }

        return true;
    };

    const value = {
        currentUser,
        loading,
        error,
        login: handleLogin,
        register: handleRegister,
        logout: handleLogout,
        validateAuth
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;